import React, { useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Progress } from 'reactstrap';
import { useTranslation } from 'react-i18next';

const UploadProgressModal = ({
  isOpen,
  isMinimized,
  toggleMinimize,
  onClose, // Optional: A function to completely close/cancel the upload
  logs,
  progress = 0, // A value from 0-100 for the progress bar
  isRTL,
}) => {
  const { t } = useTranslation();
  const latestLog = logs.length > 0 ? logs[logs.length - 1] : t('WaitingForProcessToStart');
  const logContainerRef = useRef(null);

  // Effect to auto-scroll the log container in the full-size modal
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Don't render anything if the modal is not supposed to be open
  if (!isOpen) {
    return null;
  }

  // --- RENDER MINIMIZED VIEW ---
  // This is a custom component, not a Reactstrap Modal, to allow background scrolling.
  if (isMinimized) {
    return (
      <div style={getMinimizedStyle(isRTL)} className="upload-progress-minimized">
        <div style={styles.minimizedHeader}>
          <strong style={styles.minimizedTitle}>{t('UploadProgress')}</strong>
          <div style={styles.minimizedIcons}>
            {/* Restore/Maximize Icon */}
            <i
              className="ri-arrow-up-s-line"
              style={styles.icon}
              onClick={toggleMinimize}
              title={t('Restore')}
            />
            {/* Close/Cancel Icon (only shown if onClose prop is provided) */}
            {onClose && (
              <i
                className="ri-close-line"
                style={styles.icon}
                onClick={onClose}
                title={t('Close')}
              />
            )}
          </div>
        </div>
        <div style={styles.minimizedBody} onClick={toggleMinimize}>
          <small style={styles.minimizedLog}>{latestLog}</small>
          <Progress
            value={progress}
            style={styles.progressBar}
            className="mt-1"
          />
        </div>
      </div>
    );
  }

  // --- RENDER FULL MODAL VIEW ---
  return (
    <Modal
      isOpen={true} 
      toggle={toggleMinimize} 
      centered
      backdrop="static" // Prevents closing on backdrop click
      keyboard={false} // Prevents closing with the Esc key
    >
      <ModalHeader toggle={toggleMinimize}   className={`d-flex align-items-center ${isRTL === true ? 'flex-row-reverse' : ''}`}>
        {t('UploadProgress')}
      </ModalHeader>
      <ModalBody>
        <div style={{ marginBottom: '1rem' }}>
          <Progress multi>
            <Progress bar value={progress}>{`${Math.round(progress)}%`}</Progress>
          </Progress>
        </div>
        <pre ref={logContainerRef} style={styles.logContainer}>
          {logs.length === 0 ? (
            <span>{t('WaitingForProcessToStart')}</span>
          ) : (
            logs.map((log, index) => <div key={index}>{log}</div>)
          )}
        </pre>
      </ModalBody>
    </Modal>
  );
};


// --- Dynamic and Static Styles ---

// Function to get position style based on LTR/RTL direction
const getMinimizedStyle = (isRTL) => ({
  position: 'fixed',
  bottom: '20px',
  right: isRTL ? 'auto' : '20px',
  left: isRTL ? '20px' : 'auto',
  width: '320px',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 5px 15px rgba(0,0,0,.3)',
  zIndex: 1051,
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
});

// A centralized styles object for readability
const styles = {
  minimizedHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid #eee',
    backgroundColor: '#f8f9fa',
  },
  minimizedTitle: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  minimizedIcons: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    cursor: 'pointer',
    fontSize: '1.2rem',
    marginLeft: '10px',
    color: '#6c757d',
  },
  minimizedBody: {
    padding: '8px 12px 12px 12px',
    cursor: 'pointer',
  },
  minimizedLog: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.8rem',
    color: '#495057',
  },
  progressBar: {
    height: '6px',
  },
  logContainer: {
    background: '#f4f4f4',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '15px',
    minHeight: '200px',
    maxHeight: '60vh',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    fontSize: '0.9em',
  },
};


export default UploadProgressModal;